import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import AlertSnackbar from '../../shared/ui/AlertSnackbar';
import { TransitionLeft } from '../../shared/lib/utils';
import type { NotificationOptions, NotificationSeverity } from '../types';

interface NotificationState {
  open: boolean;
  message: string;
  severity: NotificationSeverity;
  transition: typeof TransitionLeft | false;
}

interface NotificationContextValue {
  notify: (options: NotificationOptions) => void;
  close: () => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

const initialState: NotificationState = {
  open: false,
  message: '',
  severity: 'info',
  transition: false,
};

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<NotificationState>(initialState);

  const close = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const notify = useCallback(({ message, severity = 'info' }: NotificationOptions) => {
    setState({
      open: true,
      message,
      severity,
      transition: TransitionLeft,
    });
  }, []);

  const value = useMemo(() => ({ notify, close }), [notify, close]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <AlertSnackbar
        open={state.open}
        setOpen={(open: boolean) => {
          if (!open) close();
        }}
        transition={state.transition}
        message={state.message}
        severity={state.severity}
      />
    </NotificationContext.Provider>
  );
}

export function useNotification(): NotificationContextValue {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
}
